import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { WatchSessionDuration } from '@core/models/entities/watch-session.models';

import { exhaustMap, merge, Observable, Subject, tap } from 'rxjs';
import registerChromecastPlugin from '@silvermine/videojs-chromecast';
import Player from 'video.js/dist/types/player';
import videojs from 'video.js';

registerChromecastPlugin(videojs);

@Component({
  selector: 'gus-video-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPlayerComponent implements AfterViewInit, OnDestroy {
  paused: OutputEmitterRef<WatchSessionDuration> = output();

  start: OutputEmitterRef<void> = output();

  poster: InputSignal<string> = input.required();

  videoSrc: InputSignal<string> = input.required();

  player!: Player;

  readonly afterPlayerSetup$: Subject<void> = new Subject();

  #watchStart: number = 0;
  #lastTime: number = 0;

  @ViewChild('target', { static: true }) target!: ElementRef;

  constructor() {
    this.manageInputs();
  }

  ngAfterViewInit(): void {
    const defaultOptions = {
      techOrder: ['chromecast', 'html5'],
      controls: true,
      autoplay: false,
      preload: 'auto',
      responsive: true,
      aspectRatio: '16:9',
      controlBar: {
        children: [
          'playToggle',
          'volumePanel',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'progressControl',
          'remainingTimeDisplay',
          'pictureInPictureToggle',
          'fullscreenToggle',
        ],
      },
      fluid: true,
      hls: {
        maxBufferLength: 30, // Maximum buffer length in seconds
        maxMaxBufferLength: 60, // Max length of the buffer
        maxBufferSize: 60 * 1000 * 1000, // Max buffer size in bytes
        minBufferLength: 10, // Minimum buffer length
      },
      plugins: {
        chromecast: {
          addButtonToControlBar: true,
          buttonPositionIndex: -1,
        },
      },
    };

    this.player = videojs(this.target.nativeElement, { ...defaultOptions } as Parameters<typeof videojs>[1]);

    this.afterPlayerSetup$.next();

    this.bindPlayerEvents();
  }

  ngOnDestroy(): void {
    if (this.#lastTime > this.#watchStart) {
      this.paused.emit({ watchStart: this.#watchStart, watchEnd: this.#lastTime });
    }
    this.player.dispose();
  }

  private bindPlayerEvents(): void {
    this.player.on('play', () => {
      this.#watchStart = Math.floor(this.player.currentTime() || 0);
      this.start.emit();
    });

    this.player.on('pause', () => this.paused.emit({ watchStart: this.#watchStart, watchEnd: this.#lastTime }));

    this.player.on('timeupdate', () => {
      if (!this.player.seeking() || !this.player.paused()) {
        this.#lastTime = Math.floor(this.player.currentTime()!);
      }
    });
  }

  private manageInputs(): void {
    const poster: Observable<string> = toObservable(this.poster).pipe(
      tap((poster: string) => this.player.poster(poster))
    );

    const videoSrc: Observable<string> = toObservable(this.videoSrc).pipe(
      tap((src: string) => this.player.src({ src, type: 'application/x-mpegURL' }))
    );

    this.afterPlayerSetup$
      .asObservable()
      .pipe(exhaustMap(() => merge(poster, videoSrc)))
      .subscribe();
  }
}
